"use client"
import { type CryptoData, type UnifiedMessage } from "@/lib/api"
import { useTranslation } from 'react-i18next';
import CryptoIcon from '@/app/components/Dynamic/CryptoIcon';
import { formatCoMessageTime } from '@/app/utils/useFormat';
import { formatDecimalIfNeeded } from '@/lib/utils';
import { Tooltip as AntdTooltip } from "antd";
import { chainList } from "@/app/const/chain";
import Chain from '@/components/chain';
import { ChainInfo } from '@/lib/dex-api';
import { Badge } from "@/components/ui/badge";

// 消息类型：9合约交易成功、10合约交易失败、16现货交易成功、17现货交易失败
export function TransactionItem({
    transaction,
    children,
    isDropdown = false,
}: {
    transaction: UnifiedMessage,
    children?: React.ReactNode,
    isDropdown?: boolean,
}) {
    const { t } = useTranslation();
    const showTimeWithChildren = Boolean(isDropdown && children);
    
    if ([16, 17].includes(transaction.messageType)) {
        const selectedChain = chainList.find(c => c.chainId === transaction.variables.chainId);
        return (
            <>
                <div className="size-10 rounded-full flex items-center justify-center flex-shrink-0 gap-[0.5px] relative">
                    <div className="relative w-5 h-full overflow-hidden">
                        <div className="absolute top-0 left-0 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-font-primary">
                            <CryptoIcon
                                coin={{
                                    // icon: transaction.variables.fromIcon,
                                    coinSymbol: transaction.variables.fromSymbol,
                                } as unknown as CryptoData}
                                size={10}
                            />
                        </div>
                    </div>
                    <div className="relative w-5 h-full overflow-hidden">
                        <div className="absolute bottom-0 right-0 w-10 h-10 rounded-full flex items-end justify-end text-right flex-shrink-0 text-font-primary">
                            <CryptoIcon
                                coin={{
                                    // icon: transaction.variables.toIcon,
                                    coinSymbol: transaction.variables.toSymbol,
                                } as unknown as CryptoData}
                                size={10}
                                className="flex-none"
                            />
                        </div>
                    </div>
                    <AntdTooltip title={`${t('shop.networkSupport')}${selectedChain?.chainName}`}>
                        <div
                            className="absolute bottom-0 -right-1 border rounded-full p-[1px] bg-white"
                        >
                            <Chain {...(selectedChain as ChainInfo)} width={12} height={12} />
                        </div>
                    </AntdTooltip>
                </div>
                <div className="flex-1 min-w-0">
                    {showTimeWithChildren ? (
                        <div className="flex items-center justify-between gap-2 mb-1">
                            <div className="min-w-0 flex items-center gap-2 flex-wrap">{children}</div>
                            <span className="text-xs text-font-primary/50 flex-shrink-0 whitespace-nowrap">
                                {formatCoMessageTime(transaction.createTime)}
                            </span>
                        </div>
                    ): null}
                    <div className="flex items-start justify-between gap-2 mb-1">
                        <h4 className="text-sm font-medium text-font-primary line-clamp-2 break-all">
                            {!isDropdown && children}
                            <span className="ml-0.5 mr-1">{transaction.variables.fromSymbol}</span>
                            <span className="mx-1 co-number-bold">{formatDecimalIfNeeded(transaction.variables.fromAmount)}</span>
                            <span className="mx-1">{t('explore.exchangeTo')}</span>
                            <span className="mx-1">{transaction.variables.toSymbol}</span>
                            {
                                transaction.messageType === 16 && (
                                    <span className="ml-1 co-number-bold">{formatDecimalIfNeeded(transaction.variables.toAmount)}</span>
                                )
                            }
                        </h4>
                        {!showTimeWithChildren && (
                            <span className="text-xs text-font-primary/50 flex-shrink-0 whitespace-nowrap mt-0.5">
                                {formatCoMessageTime(transaction.createTime)}
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-1">
                        {
                            transaction.messageType === 16 ? (
                                <Badge variant="default-outline" size="xxs">
                                    {t('explore.filled')}
                                </Badge>
                            ) : (
                                <Badge variant="danger-outline" size="xxs">
                                    {t('explore.failed')}
                                </Badge>
                            )
                        }
                    </div>
                </div>
            </>
        )
    }
    return (
        <>
            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-font-primary">
                <CryptoIcon
                    coin={{
                        coinSymbol: transaction.variables.symbol,
                    } as unknown as CryptoData}
                    size={10}
                />
            </div>
            <div className="flex-1 min-w-0">
                {showTimeWithChildren ? (
                    <div className="flex items-center justify-between gap-2 mb-1">
                        <div className="min-w-0 flex items-center gap-2 flex-wrap">{children}</div>
                        <span className="text-xs text-font-primary/50 flex-shrink-0 whitespace-nowrap">
                            {formatCoMessageTime(transaction.createTime)}
                        </span>
                    </div>
                ): null}
                <div className="flex items-start justify-between gap-2 mb-1">
                    <h4 className="text-sm font-medium text-font-primary line-clamp-1 flex items-center gap-1">
                        {!isDropdown && children}
                        <span className="mx-0.5">{transaction.variables.symbol}</span>
                        <span className="mx-0.5">
                            {transaction.variables.side === 'Long' && t('liveDetail.buyOpen')}
                            {transaction.variables.side === 'Short' && t('liveDetail.sellOpen')}
                            {transaction.variables.side === 'Close Long' && t('liveDetail.sellClose')}
                            {transaction.variables.side === 'Close Short' && t('liveDetail.buyClose')}
                        </span>
                        <span className="co-number-bold">
                            {formatDecimalIfNeeded(transaction.variables.filledQty)}
                        </span>
                        <span className="mx-0.5 text-xxs text-font-secondary">{transaction.variables.qtyUnit}</span>
                    </h4>
                    {!showTimeWithChildren && (
                        <span className="text-xs text-font-primary/50 flex-shrink-0 whitespace-nowrap">
                            {formatCoMessageTime(transaction.createTime)}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-1">
                    {
                        transaction.messageType === 9 ? (
                            <Badge variant="default-outline" size="xxs">
                                {t('explore.filled')}
                            </Badge>
                        ) : (
                            <Badge variant="danger-outline" size="xxs">
                                {t('explore.failed')}
                            </Badge>
                        )
                    }
                    <p className="text-xs text-font-primary/70 line-clamp-2">
                        {t('explore.avgPrice', { avgPrice: transaction.variables.avgPrice })}
                    </p>
                </div>
            </div>
        </>
    )
}